import type { Customer, CustomerStatus } from '@central/api';
import {
  Card,
  StatusChip,
  color,
  formatDate,
  formatMonth,
  initials,
  HealthChip,
  InfoRow,
  logRetention,
} from '@central/ui';
import { Button, Group, Menu, Table, Tabs, VisuallyHidden } from '@mantine/core';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { keys, useApi } from '../api';
import { shortKey, useEnvironmentPages } from '../lib/environment';
import { Crumbs, DataTable, Empty, LoadMore, QueryState } from '../components/parts';
import { customerStatusChip, enrollmentLabel, logAccessLabel, mono, muted } from '../lib/labels';
import {
  customerTabs,
  renewalChip,
  ssoSummary,
  statusMove,
  statusMoves,
  tabLabel,
  type CustomerTab,
} from '../lib/customer';
import { formatIn } from '../lib/format';
import { activationsText } from '../lib/license';
import { useCanWrite } from '../lib/me';
import { AuditRow } from './Audit';
import {
  CustomerDetailsModal,
  CustomerStatusModal,
  MaintenanceModal,
  NewEnvironmentModal,
  NewLicenseModal,
  SettingsModal,
} from './CustomerDialogs';
import { InviteModal, PortalUsers } from './CustomerTeam';
import { CustomerSso } from './CustomerSso';

type Dialog = 'maintenance' | 'settings' | 'environment' | 'license' | 'invite' | 'details';

/**
 * One customer (docs/design/AdminCustomer.dc.html): a header with the
 * customer's actions, then sections as tabs (`?tab=`) with the list sizes.
 */
export function CustomerDetail() {
  const api = useApi();
  const { customerId } = useParams({ from: '/app/customers/$customerId' });
  const customer = useQuery({
    queryKey: keys.customer(customerId),
    queryFn: () => api.customers.get(customerId),
  });
  return (
    <>
      <Crumbs
        items={[{ label: 'Customers', to: '/customers' }, { label: customer.data?.name ?? '…' }]}
      />
      <QueryState isPending={customer.isPending} error={customer.error}>
        {() => <Body c={customer.data!} />}
      </QueryState>
    </>
  );
}

function Body({ c }: { c: Customer }) {
  const api = useApi();
  const canWrite = useCanWrite();
  const navigate = useNavigate({ from: '/customers/$customerId' });
  const tab = useSearch({ from: '/app/customers/$customerId' }).tab ?? 'overview';
  const show = (t: CustomerTab) =>
    void navigate({ search: { tab: t === 'overview' ? undefined : t }, replace: true });
  const [dialog, setDialog] = useState<Dialog>();
  const [moveTo, setMoveTo] = useState<CustomerStatus>();
  const close = () => setDialog(undefined);
  const team = useQuery({
    queryKey: keys.customerUsers(c.id),
    queryFn: () => api.customers.users(c.id),
  });
  const counts = useQuery({
    queryKey: keys.customerCounts(c.id),
    queryFn: () => api.customers.counts(c.id),
  });
  const hasOwner = !!team.data?.users.some((u) => u.role === 'Owner' && u.enabled);
  const chip = customerStatusChip[c.status];
  const panel = (children: ReactNode) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 20 }}>
      {children}
    </div>
  );

  return (
    <>
      <header
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', minWidth: 0 }}>
          <div
            aria-hidden
            style={{
              width: 48,
              height: 48,
              flex: 'none',
              borderRadius: 10,
              background: color.link,
              color: color.surface,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
            }}
          >
            {initials(c.name)}
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 26, lineHeight: '34px' }}>{c.name}</h1>
            <div
              style={{
                fontSize: 14,
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                flexWrap: 'wrap',
                ...muted,
              }}
            >
              <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
              <SupportedBy c={c} />
              <span>
                Customer since {formatMonth(c.createdAt)}
                {c.accountManager && ` · Account manager: ${c.accountManager.name}`}
              </span>
            </div>
          </div>
        </div>
        {canWrite && (
          <Group gap={10} wrap="wrap">
            <Button variant="default" h={40} onClick={() => setDialog('invite')}>
              Invite portal user
            </Button>
            <Button variant="default" h={40} onClick={() => setDialog('environment')}>
              New environment
            </Button>
            <Button color="dark" h={40} onClick={() => setDialog('license')}>
              New license
            </Button>
            <Menu position="bottom-end">
              <Menu.Target>
                <Button variant="default" h={40} aria-label="Customer actions">
                  ⋯
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => setDialog('details')}>Edit name and manager</Menu.Item>
                {statusMoves[c.status].map((s) => (
                  <Menu.Item
                    key={s}
                    color={s === 'active' ? undefined : 'red'}
                    onClick={() => setMoveTo(s)}
                  >
                    {statusMove[s].verb}…
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </Group>
        )}
      </header>
      {/* Mounted only while open, so each opening starts from the stored customer. */}
      {dialog === 'maintenance' && <MaintenanceModal c={c} opened onClose={close} />}
      {dialog === 'settings' && <SettingsModal c={c} opened onClose={close} />}
      {dialog === 'environment' && <NewEnvironmentModal c={c} opened onClose={close} />}
      {dialog === 'license' && <NewLicenseModal customerId={c.id} opened onClose={close} />}
      {dialog === 'details' && <CustomerDetailsModal c={c} onClose={close} />}
      {moveTo && <CustomerStatusModal c={c} to={moveTo} onClose={() => setMoveTo(undefined)} />}
      <InviteModal
        customerId={c.id}
        opened={dialog === 'invite'}
        onClose={close}
        hasOwner={hasOwner}
      />

      <Tabs value={tab} keepMounted={false} onChange={(v) => v && show(v as CustomerTab)}>
        <Tabs.List aria-label="Customer sections">
          {(Object.keys(customerTabs) as CustomerTab[]).map((t) => (
            <Tabs.Tab key={t} value={t}>
              {tabLabel(t, counts.data)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Tabs.Panel value="overview">
          {panel(
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <MaintenanceCard
                  c={c}
                  onEdit={canWrite ? () => setDialog('maintenance') : undefined}
                />
                <SettingsCard
                  c={c}
                  onEdit={canWrite ? () => setDialog('settings') : undefined}
                  onSso={() => show('users')}
                />
              </div>
              <Environments c={c} />
              <PortalUsers customerId={c.id} />
            </>,
          )}
        </Tabs.Panel>
        <Tabs.Panel value="environments">{panel(<Environments c={c} />)}</Tabs.Panel>
        <Tabs.Panel value="licenses">{panel(<Licenses c={c} />)}</Tabs.Panel>
        <Tabs.Panel value="maintenance">
          {panel(
            <MaintenanceCard
              c={c}
              full
              onEdit={canWrite ? () => setDialog('maintenance') : undefined}
            />,
          )}
        </Tabs.Panel>
        <Tabs.Panel value="users">
          {panel(
            <>
              <PortalUsers customerId={c.id} />
              <CustomerSso customerId={c.id} />
            </>,
          )}
        </Tabs.Panel>
        <Tabs.Panel value="audit">{panel(<CustomerAudit c={c} />)}</Tabs.Panel>
      </Tabs>
    </>
  );
}

/**
 * The maintenance plan: term, release line, crash reports and renewal; `full`
 * (the Maintenance tab) adds when the term ends and the renewal note.
 */
function MaintenanceCard({
  c,
  full,
  onEdit,
}: {
  c: Customer;
  full?: boolean;
  onEdit?: () => void;
}) {
  const m = c.maintenance;
  const renewal = renewalChip(m.renewal);
  return (
    <Card
      title="Maintenance plan"
      action={onEdit && <EditButton what="maintenance plan" onClick={onEdit} />}
      style={{ flex: '1 1 300px' }}
    >
      <InfoRow label="Term">
        {m.startsAt || m.endsAt ? `${formatDate(m.startsAt)} – ${formatDate(m.endsAt)}` : 'No plan'}
      </InfoRow>
      <InfoRow label="Covers releases through">
        <span style={mono}>{m.coversThrough ?? '–'}</span>
      </InfoRow>
      {full && m.endsAt && <InfoRow label="Ends">{formatIn(m.endsAt)}</InfoRow>}
      <InfoRow label="Automatic crash reports">
        {c.settings.crashAutoSend ? 'Allowed by customer' : 'Off'}
      </InfoRow>
      <InfoRow label="Renewal">
        <StatusChip tone={renewal.tone}>{renewal.label}</StatusChip>
      </InfoRow>
      {full && m.renewal?.note && <InfoRow label="Renewal note">{m.renewal.note}</InfoRow>}
    </Card>
  );
}

/** Portal settings: the ones staff edit, the SSO summary and log retention. */
function SettingsCard({
  c,
  onEdit,
  onSso,
}: {
  c: Customer;
  onEdit?: () => void;
  onSso: () => void;
}) {
  const api = useApi();
  const sso = useQuery({
    queryKey: keys.customerSso(c.id),
    queryFn: () => api.customers.sso.list(c.id),
  });
  return (
    <Card
      title="Portal settings"
      action={onEdit && <EditButton what="portal settings" onClick={onEdit} />}
      style={{ flex: '1 1 300px' }}
    >
      <InfoRow label="Require MFA for every user">{c.settings.requireMfa ? 'On' : 'Off'}</InfoRow>
      <InfoRow label="Who can view logs">{logAccessLabel[c.settings.logAccess]}</InfoRow>
      <InfoRow label="Deployment health emails">
        {c.healthEmails ? 'Staff and customer' : 'Staff only'}
      </InfoRow>
      <InfoRow label="Organization SSO">
        {sso.data ? ssoSummary(sso.data.items) : sso.isError ? 'Unavailable' : '…'}
        {' · '}
        <Button variant="subtle" size="compact-sm" px={2} onClick={onSso}>
          {onEdit ? 'Edit' : 'View'} <VisuallyHidden>single sign-on</VisuallyHidden>
        </Button>
      </InfoRow>
      <InfoRow label="Log retention">{logRetention}</InfoRow>
    </Card>
  );
}

function Environments({ c }: { c: Customer }) {
  const envs = useEnvironmentPages({ customerId: c.id });
  const items = envs.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card title="Environments" padded={false}>
      <QueryState isPending={envs.isPending} error={envs.error}>
        {() =>
          items.length === 0 ? (
            <Empty>No environments yet.</Empty>
          ) : (
            <DataTable
              label="Environments"
              minWidth={820}
              head={['Name', 'Type', 'Health', 'Version', 'License', 'Enrollment', '']}
            >
              {items.map((e) => {
                const key = e.customers.find((x) => x.id === c.id)?.licenseKey;
                return (
                  <Table.Tr key={e.id}>
                    <Table.Td style={{ fontWeight: 600 }}>{e.name}</Table.Td>
                    <Table.Td>
                      {e.kind === 'dedicated'
                        ? 'Dedicated'
                        : `Shared · ${e.customers.length} customers`}
                    </Table.Td>
                    <Table.Td>
                      <HealthChip state={e.health} />
                    </Table.Td>
                    <Table.Td style={mono}>{e.version ?? '–'}</Table.Td>
                    <Table.Td style={mono}>
                      {key ? <span title={key}>{shortKey(key)}</span> : '–'}
                    </Table.Td>
                    <Table.Td>
                      {e.enrollmentStatus === 'enrolled' && e.enrolledAt
                        ? `Enrolled ${formatDate(e.enrolledAt)}`
                        : enrollmentLabel[e.enrollmentStatus]}
                    </Table.Td>
                    <Table.Td>
                      <Link to="/environments/$environmentId" params={{ environmentId: e.id }}>
                        Open <VisuallyHidden>{e.name}</VisuallyHidden>
                      </Link>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </DataTable>
          )
        }
      </QueryState>
      {envs.hasNextPage && (
        <LoadMore
          what="environments"
          loading={envs.isFetchingNextPage}
          onClick={() => void envs.fetchNextPage()}
        />
      )}
    </Card>
  );
}

function Licenses({ c }: { c: Customer }) {
  const api = useApi();
  const licenses = useInfiniteQuery({
    queryKey: keys.licenseList(c.id),
    queryFn: ({ pageParam }) => api.licenses.list({ customerId: c.id, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = licenses.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card title="Licenses" padded={false}>
      <QueryState isPending={licenses.isPending} error={licenses.error}>
        {() =>
          items.length === 0 ? (
            <Empty>No licenses yet.</Empty>
          ) : (
            <DataTable label="Licenses" head={['Key', 'Term', 'Activations', '']}>
              {items.map((l) => (
                <Table.Tr key={l.id}>
                  <Table.Td>
                    <span style={mono}>{l.key}</span>
                    {l.suspended && (
                      <>
                        {' '}
                        <StatusChip tone="down">Suspended</StatusChip>
                      </>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {formatDate(l.startsAt)} – {formatDate(l.expiresAt)}
                  </Table.Td>
                  <Table.Td style={mono}>
                    {l.activeActivations} of {activationsText(l.allowedActivations)}
                  </Table.Td>
                  <Table.Td>
                    <Link to="/licenses/$licenseId" params={{ licenseId: l.id }}>
                      Edit <VisuallyHidden>{l.key}</VisuallyHidden>
                    </Link>
                  </Table.Td>
                </Table.Tr>
              ))}
            </DataTable>
          )
        }
      </QueryState>
      {licenses.hasNextPage && (
        <LoadMore
          what="licenses"
          loading={licenses.isFetchingNextPage}
          onClick={() => void licenses.fetchNextPage()}
        />
      )}
    </Card>
  );
}

/** The customer's audit events, newest first; the full log page has the filters. */
function CustomerAudit({ c }: { c: Customer }) {
  const api = useApi();
  const q = { customerId: c.id };
  const list = useInfiniteQuery({
    queryKey: keys.auditList(q),
    queryFn: ({ pageParam }) => api.audit.list({ ...q, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card
      title="Audit log"
      padded={false}
      action={
        <Link to="/audit" search={{ customerId: c.id }} style={{ fontSize: 13 }}>
          Filter in the audit log
        </Link>
      }
    >
      <QueryState isPending={list.isPending} error={list.error}>
        {() =>
          items.length === 0 ? (
            <Empty>Nothing has been recorded for this customer yet.</Empty>
          ) : (
            <DataTable
              label="Audit events"
              minWidth={900}
              head={['When', 'Actor', 'Action', 'Target', 'Changes', 'IP']}
            >
              {items.map((e) => (
                <AuditRow key={e.id} e={e} />
              ))}
            </DataTable>
          )
        }
      </QueryState>
      {list.hasNextPage && (
        <LoadMore
          what="audit events"
          loading={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        />
      )}
    </Card>
  );
}

function EditButton({ what, onClick }: { what: string; onClick: () => void }) {
  return (
    <Button variant="subtle" size="xs" onClick={onClick}>
      Edit <VisuallyHidden>{what}</VisuallyHidden>
    </Button>
  );
}

/** Who gives the customer tier 1 support: its reseller, linking to its page, or Rethink. */
function SupportedBy({ c }: { c: Customer }) {
  if (!c.resellerId) return <span style={{ color: color.text }}>Direct customer</span>;
  return (
    <span style={{ color: color.text }}>
      Supported by{' '}
      {c.reseller ? (
        <Link
          to="/resellers/$resellerId"
          params={{ resellerId: c.reseller.id }}
          style={{ fontWeight: 600 }}
        >
          {c.reseller.name}
        </Link>
      ) : (
        'a reseller that no longer exists'
      )}
    </span>
  );
}
